import { env } from '../config/env';
import type {
  ApiResponse,
  ApiErrorResponse,
  AuthSessionDto,
  AuthTokensDto,
  CampaignListResponse,
  CampaignResponse,
  CampaignValidationResult,
  CreateCampaignRequest,
  HealthCheckResponse,
  InboxMessageDetail,
  InviteMemberRequest,
  LoginRequest,
  MailboxListResponse,
  MailboxResponse,
  MarkMessageReadResponse,
  OrganizationDto,
  OrganizationMemberDto,
  PaginatedInboxResponse,
  RegisterRequest,
  SendMessageRequest,
  SendMessageResponse,
  TransferOwnershipRequest,
  TransitionCampaignStatusRequest,
  UpdateCampaignRequest,
  UpdateMemberRoleRequest,
  UpdateOrganizationRequest,
  UserDto,
  AdminOrganizationDto,
  AdminUserDto,
  ApiKeyDto,
  CreateApiKeyRequest,
  CreateApiKeyResponse,
  InvoiceDto,
  OnboardingStatusDto,
  OrganizationUsageDto,
  PlanDto,
  RotateApiKeyResponse,
  SubscriptionDto,
  DeliverabilityHealthSummaryDto,
  DomainReadinessReportDto,
  WarmupProfileDto,
  UpdateWarmupConfigRequest,
  IpPoolDto,
  DedicatedIpDto,
  CreateIpPoolRequest,
  AddIpToPoolRequest,
} from '@antigravity/types';

export class ApiClientError extends Error {
  public readonly statusCode: number;
  public readonly errorDetails: ApiErrorResponse | null;

  constructor(
    message: string,
    statusCode: number,
    errorDetails: ApiErrorResponse | null = null,
  ) {
    super(message);
    this.name = 'ApiClientError';
    this.statusCode = statusCode;
    this.errorDetails = errorDetails;
  }
}

class ApiClient {
  private readonly baseUrl: string;
  private accessToken: string | null = null;
  private currentOrgId: string | null = null;
  private isRefreshing = false;
  private refreshSubscribers: Array<(token: string) => void> = [];

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl.replace(/\/$/, '');
  }

  public setAccessToken(token: string | null): void {
    this.accessToken = token;
  }

  public getAccessToken(): string | null {
    return this.accessToken;
  }

  public setCurrentOrganizationId(orgId: string | null): void {
    this.currentOrgId = orgId;
  }

  private onTokenRefreshed(token: string): void {
    this.refreshSubscribers.forEach((cb) => cb(token));
    this.refreshSubscribers = [];
  }

  private subscribeTokenRefresh(cb: (token: string) => void): void {
    this.refreshSubscribers.push(cb);
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    retryOnAuthFailure = true,
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint.startsWith('/') ? endpoint : `/${endpoint}`}`;

    const headers = new Headers(options.headers || {});
    if (!headers.has('Content-Type') && !(options.body instanceof FormData)) {
      headers.set('Content-Type', 'application/json');
    }
    headers.set('Accept', 'application/json');

    // Attach in-memory bearer token if present
    if (this.accessToken && !headers.has('Authorization')) {
      headers.set('Authorization', `Bearer ${this.accessToken}`);
    }

    // Attach organization context if selected
    if (this.currentOrgId && !headers.has('x-organization-id')) {
      headers.set('x-organization-id', this.currentOrgId);
    }

    try {
      const response = await fetch(url, {
        ...options,
        headers,
        credentials: 'include', // Include HttpOnly cookies (ag_access_token, ag_refresh_token)
      });

      // Handle 401 Token Expiration with Automatic Refresh
      if (
        response.status === 401 &&
        retryOnAuthFailure &&
        !endpoint.includes('/auth/login') &&
        !endpoint.includes('/auth/refresh')
      ) {
        if (!this.isRefreshing) {
          this.isRefreshing = true;
          try {
            const newTokens = await this.refreshToken();
            this.setAccessToken(newTokens.accessToken);
            this.isRefreshing = false;
            this.onTokenRefreshed(newTokens.accessToken);
          } catch (refreshErr) {
            this.isRefreshing = false;
            this.setAccessToken(null);
            throw refreshErr;
          }
        }

        // Wait for token refresh to complete then retry original request
        return new Promise<T>((resolve, reject) => {
          this.subscribeTokenRefresh(async (newToken: string) => {
            try {
              const retryHeaders = new Headers(options.headers || {});
              retryHeaders.set('Authorization', `Bearer ${newToken}`);
              const res = await this.request<T>(
                endpoint,
                { ...options, headers: retryHeaders },
                false,
              );
              resolve(res);
            } catch (err) {
              reject(err);
            }
          });
        });
      }

      const data = await response.json().catch(() => null);

      if (!response.ok) {
        const errorMsg = data?.message
          ? Array.isArray(data.message)
            ? data.message.join(', ')
            : data.message
          : `HTTP error ${response.status}: ${response.statusText}`;
        throw new ApiClientError(
          errorMsg,
          response.status,
          data as ApiErrorResponse,
        );
      }

      if (
        data &&
        typeof data === 'object' &&
        'success' in data &&
        'data' in data
      ) {
        return (data as ApiResponse<T>).data;
      }

      return data as T;
    } catch (err) {
      if (err instanceof ApiClientError) {
        throw err;
      }
      throw new ApiClientError(
        (err as Error).message || 'Network request failed',
        0,
        null,
      );
    }
  }

  public get<T>(endpoint: string, options?: RequestInit): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'GET' });
  }

  public post<T>(
    endpoint: string,
    body?: unknown,
    options?: RequestInit,
  ): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  public put<T>(
    endpoint: string,
    body?: unknown,
    options?: RequestInit,
  ): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  public patch<T>(
    endpoint: string,
    body?: unknown,
    options?: RequestInit,
  ): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PATCH',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  public delete<T>(endpoint: string, options?: RequestInit): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'DELETE' });
  }

  // ==========================================
  // AUTHENTICATION APIS
  // ==========================================

  public async register(payload: RegisterRequest): Promise<AuthSessionDto> {
    const session = await this.post<AuthSessionDto>('/auth/register', payload);
    this.setAccessToken(session.tokens.accessToken);
    return session;
  }

  public async login(payload: LoginRequest): Promise<AuthSessionDto> {
    const session = await this.post<AuthSessionDto>('/auth/login', payload);
    this.setAccessToken(session.tokens.accessToken);
    return session;
  }

  public async refreshToken(token?: string): Promise<AuthTokensDto> {
    return this.post<AuthTokensDto>('/auth/refresh', {
      refreshToken: token || '',
    });
  }

  public async logout(refreshToken?: string): Promise<{ message: string }> {
    try {
      return await this.post<{ message: string }>('/auth/logout', {
        refreshToken,
      });
    } finally {
      this.setAccessToken(null);
      this.setCurrentOrganizationId(null);
    }
  }

  public async getMe(): Promise<{
    user: UserDto;
    organizations: Array<{
      id: string;
      name: string;
      slug: string;
      role: any;
    }>;
  }> {
    return this.get('/auth/me');
  }

  // ==========================================
  // ORGANIZATION APIS
  // ==========================================

  public async listOrganizations(): Promise<
    Array<{
      id: string;
      name: string;
      slug: string;
      role: any;
      memberCount: number;
    }>
  > {
    return this.get('/organizations');
  }

  public async createOrganization(data: {
    name: string;
    slug?: string;
  }): Promise<OrganizationDto> {
    return this.post<OrganizationDto>('/organizations', data);
  }

  public async getOrganization(orgId: string): Promise<
    OrganizationDto & {
      memberCount: number;
    }
  > {
    return this.get(`/organizations/${orgId}`);
  }

  public async updateOrganization(
    orgId: string,
    data: UpdateOrganizationRequest,
  ): Promise<OrganizationDto> {
    return this.patch<OrganizationDto>(`/organizations/${orgId}`, data);
  }

  public async listMembers(orgId: string): Promise<OrganizationMemberDto[]> {
    return this.get<OrganizationMemberDto[]>(`/organizations/${orgId}/members`);
  }

  public async inviteMember(
    orgId: string,
    data: InviteMemberRequest,
  ): Promise<any> {
    return this.post(`/organizations/${orgId}/members/invite`, data);
  }

  public async updateMemberRole(
    orgId: string,
    memberId: string,
    data: UpdateMemberRoleRequest,
  ): Promise<OrganizationMemberDto> {
    return this.patch<OrganizationMemberDto>(
      `/organizations/${orgId}/members/${memberId}/role`,
      data,
    );
  }

  public async removeMember(
    orgId: string,
    memberId: string,
  ): Promise<{ message: string }> {
    return this.delete<{ message: string }>(
      `/organizations/${orgId}/members/${memberId}`,
    );
  }

  public async transferOwnership(
    orgId: string,
    data: TransferOwnershipRequest,
  ): Promise<{ message: string; previousOwnerId: string; newOwnerId: string }> {
    return this.post(`/organizations/${orgId}/transfer-ownership`, data);
  }

  public async acceptInvitation(
    token: string,
  ): Promise<OrganizationMemberDto> {
    return this.post<OrganizationMemberDto>(
      `/organizations/invitations/${token}/accept`,
    );
  }

  // ==========================================
  // USER ACCESS MANAGEMENT (UAM) APIS
  // ==========================================

  public async getUamScreens(): Promise<any[]> {
    const res = await this.get<any>('/uam/screens');
    return Array.isArray(res) ? res : res?.data || [];
  }

  public async getUamRolePermissions(role?: string): Promise<any[]> {
    const query = role ? `?role=${role}` : '';
    const res = await this.get<any>(`/uam/role-permissions${query}`);
    return Array.isArray(res) ? res : res?.data || [];
  }

  public async updateUamRolePermission(role: string, permissionId: string, isGranted: boolean): Promise<any> {
    return this.patch('/uam/role-permissions', { role, permissionId, isGranted });
  }

  public async updateUamRolePermissionsBatch(
    updates: Array<{ role: string; permissionId: string; isGranted: boolean }>,
  ): Promise<any> {
    return this.put('/uam/role-permissions/batch', { updates });
  }

  public async getUamRoleTable(role: string): Promise<any[]> {
    const res = await this.get<any>(`/uam/roles/${role}/table`);
    return Array.isArray(res) ? res : res?.data || [];
  }

  public async getUamAllRoleTables(): Promise<Record<string, any>> {
    const res = await this.get<any>('/uam/roles/all-tables');
    return res && typeof res === 'object' && ('SUPER_ADMIN' in res || 'data' in res)
      ? ('data' in res ? res.data : res)
      : {};
  }

  public async getUamMyPermissions(): Promise<{
    role: string;
    isSuperAdmin: boolean;
    allowedRoutes: string[];
    deniedRoutes: string[];
    permissions: Record<string, boolean>;
  }> {
    const res = await this.get<any>('/uam/my-permissions');
    return res?.data || res || {
      role: 'VIEWER',
      isSuperAdmin: false,
      allowedRoutes: ['/webmail', '/campaigns', '/deliverability', '/billing', '/api-keys', '/uam', '/health'],
      deniedRoutes: [],
      permissions: {},
    };
  }



  // ==========================================
  // MAILBOX & WEBMAIL APIS
  // ==========================================

  public async createSendingDomain(domain: string): Promise<any> {
    return this.post('/domains', { domain });
  }

  public async listSendingDomains(): Promise<any[]> {
    return this.get<any[]>('/domains');
  }

  public async verifySendingDomain(domainId: string): Promise<any> {
    return this.post(`/domains/${domainId}/verify`, {});
  }

  public async createMailbox(payload: {
    sendingDomainId: string;
    localPart?: string;
    emailAddress?: string;
    displayName?: string;
  }): Promise<MailboxResponse> {
    return this.post<MailboxResponse>('/mailboxes', payload);
  }

  public async listMailboxes(params?: {
    page?: number;
    limit?: number;
    status?: string;
  }): Promise<MailboxListResponse> {

    const query = new URLSearchParams();
    if (params?.page) query.set('page', String(params.page));
    if (params?.limit) query.set('limit', String(params.limit));
    if (params?.status) query.set('status', params.status);
    const queryString = query.toString();
    return this.get<MailboxListResponse>(
      `/mailboxes${queryString ? `?${queryString}` : ''}`,
    );
  }

  public async getMailbox(mailboxId: string): Promise<MailboxResponse> {
    return this.get<MailboxResponse>(`/mailboxes/${mailboxId}`);
  }

  public async getInboxMessages(
    mailboxId: string,
    params?: { page?: number; limit?: number; search?: string },
  ): Promise<PaginatedInboxResponse> {
    const query = new URLSearchParams();
    if (params?.page) query.set('page', String(params.page));
    if (params?.limit) query.set('limit', String(params.limit));
    if (params?.search) query.set('search', params.search);
    const queryString = query.toString();
    return this.get<PaginatedInboxResponse>(
      `/mailboxes/${mailboxId}/messages${queryString ? `?${queryString}` : ''}`,
    );
  }

  public async getMessageDetail(
    mailboxId: string,
    messageId: string,
  ): Promise<InboxMessageDetail> {
    return this.get<InboxMessageDetail>(
      `/mailboxes/${mailboxId}/messages/${messageId}`,
    );
  }

  public async markMessageRead(
    mailboxId: string,
    messageId: string,
  ): Promise<MarkMessageReadResponse> {
    return this.post<MarkMessageReadResponse>(
      `/mailboxes/${mailboxId}/messages/${messageId}/read`,
      {},
    );
  }

  public async sendMessage(
    mailboxId: string,
    data: SendMessageRequest,
  ): Promise<SendMessageResponse> {
    return this.post<SendMessageResponse>(
      `/mailboxes/${mailboxId}/messages`,
      data,
    );
  }

  // ==========================================
  // CAMPAIGNS API (CHUNK 06A)
  // ==========================================

  public async listCampaigns(params?: {
    page?: number;
    limit?: number;
    status?: string;
    search?: string;
    fromMailboxId?: string;
  }): Promise<CampaignListResponse> {
    const query = new URLSearchParams();
    if (params?.page) query.set('page', String(params.page));
    if (params?.limit) query.set('limit', String(params.limit));
    if (params?.status) query.set('status', params.status);
    if (params?.search) query.set('search', params.search);
    if (params?.fromMailboxId) query.set('fromMailboxId', params.fromMailboxId);

    const queryString = query.toString();
    return this.get<CampaignListResponse>(
      `/campaigns${queryString ? `?${queryString}` : ''}`,
    );
  }

  public async getCampaign(id: string): Promise<CampaignResponse> {
    return this.get<CampaignResponse>(`/campaigns/${id}`);
  }

  public async createCampaign(
    data: CreateCampaignRequest,
  ): Promise<CampaignResponse> {
    return this.post<CampaignResponse>('/campaigns', data);
  }

  public async updateCampaign(
    id: string,
    data: UpdateCampaignRequest,
  ): Promise<CampaignResponse> {
    return this.patch<CampaignResponse>(`/campaigns/${id}`, data);
  }

  public async deleteCampaign(
    id: string,
  ): Promise<{ success: boolean; message: string }> {
    return this.delete<{ success: boolean; message: string }>(
      `/campaigns/${id}`,
    );
  }

  public async validateCampaign(
    id: string,
  ): Promise<CampaignValidationResult> {
    return this.post<CampaignValidationResult>(`/campaigns/${id}/validate`, {});
  }

  public async transitionCampaignStatus(
    id: string,
    data: TransitionCampaignStatusRequest,
  ): Promise<CampaignResponse> {
    return this.post<CampaignResponse>(`/campaigns/${id}/status`, data);
  }

  // ==========================================
  // CHUNK 10: BILLING & USAGE QUOTAS
  // ==========================================

  public async getPlans(): Promise<PlanDto[]> {
    return this.get<PlanDto[]>('/billing/plans');
  }

  public async getSubscription(): Promise<SubscriptionDto | null> {
    return this.get<SubscriptionDto | null>('/billing/subscription');
  }

  public async getUsage(): Promise<OrganizationUsageDto> {
    return this.get<OrganizationUsageDto>('/billing/usage');
  }

  public async subscribe(planCode: string, billingInterval: 'MONTHLY' | 'YEARLY' = 'MONTHLY'): Promise<SubscriptionDto> {
    return this.post<SubscriptionDto>('/billing/subscribe', { planCode, billingInterval });
  }

  public async cancelSubscription(cancelImmediately = false, reason?: string): Promise<SubscriptionDto> {
    return this.post<SubscriptionDto>('/billing/cancel', { cancelImmediately, reason });
  }

  public async getInvoices(): Promise<InvoiceDto[]> {
    return this.get<InvoiceDto[]>('/billing/invoices');
  }

  // ==========================================
  // CHUNK 10: API KEYS
  // ==========================================

  public async getApiKeys(): Promise<ApiKeyDto[]> {
    return this.get<ApiKeyDto[]>('/api-keys');
  }

  public async createApiKey(data: CreateApiKeyRequest): Promise<CreateApiKeyResponse> {
    return this.post<CreateApiKeyResponse>('/api-keys', data);
  }

  public async revokeApiKey(id: string): Promise<ApiKeyDto> {
    return this.delete<ApiKeyDto>(`/api-keys/${id}`);
  }

  public async rotateApiKey(id: string): Promise<RotateApiKeyResponse> {
    return this.post<RotateApiKeyResponse>(`/api-keys/${id}/rotate`, {});
  }

  // ==========================================
  // CHUNK 10: SUPER-ADMIN
  // ==========================================

  public async getAdminOrganizations(
    page = 1,
    limit = 20,
    search?: string,
  ): Promise<{ items: AdminOrganizationDto[]; total: number; page: number; limit: number }> {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (search) params.append('search', search);
    return this.get<{ items: AdminOrganizationDto[]; total: number; page: number; limit: number }>(
      `/admin/organizations?${params.toString()}`,
    );
  }

  public async updateAdminOrgStatus(
    id: string,
    status: 'ACTIVE' | 'SUSPENDED',
    reason?: string,
  ): Promise<{ id: string; status: string }> {
    return this.patch<{ id: string; status: string }>(`/admin/organizations/${id}/status`, { status, reason });
  }

  public async assignAdminOrgPlan(
    id: string,
    planCode: string,
    billingInterval: 'MONTHLY' | 'YEARLY' = 'MONTHLY',
  ): Promise<{ organizationId: string; planCode: string; status: string }> {
    return this.post<{ organizationId: string; planCode: string; status: string }>(
      `/admin/organizations/${id}/plan`,
      { planCode, billingInterval },
    );
  }

  public async getAdminUsers(
    page = 1,
    limit = 20,
  ): Promise<{ items: AdminUserDto[]; total: number; page: number; limit: number }> {
    return this.get<{ items: AdminUserDto[]; total: number; page: number; limit: number }>(
      `/admin/users?page=${page}&limit=${limit}`,
    );
  }

  public async updateAdminUserStatus(
    id: string,
    status: 'ACTIVE' | 'SUSPENDED' | 'PENDING',
    reason?: string,
  ): Promise<{ id: string; status: string }> {
    return this.patch<{ id: string; status: string }>(`/admin/users/${id}/status`, { status, reason });
  }

  public async getAdminStats(): Promise<{
    totalOrganizations: number;
    totalUsers: number;
    totalMailboxes: number;
    totalCampaigns: number;
    totalActiveSubscriptions: number;
  }> {
    return this.get<any>('/admin/stats');
  }

  // ==========================================
  // CHUNK 10: ONBOARDING
  // ==========================================

  public async getOnboardingStatus(orgId: string): Promise<OnboardingStatusDto> {
    return this.get<OnboardingStatusDto>(`/organizations/${orgId}/onboarding`);
  }

  // ==========================================
  // CHUNK 11: DELIVERABILITY, WARMUP & IP POOLS
  // ==========================================

  public async getDeliverabilityHealth(days = 30): Promise<DeliverabilityHealthSummaryDto> {
    return this.get<DeliverabilityHealthSummaryDto>(`/deliverability/health?days=${days}`);
  }

  public async getDomainReadiness(domainId: string): Promise<DomainReadinessReportDto> {
    return this.get<DomainReadinessReportDto>(`/deliverability/domains/${domainId}/readiness`);
  }

  public async getWarmupProfiles(): Promise<WarmupProfileDto[]> {
    return this.get<WarmupProfileDto[]>('/deliverability/warmup');
  }

  public async getWarmupProfile(mailboxId: string): Promise<WarmupProfileDto> {
    return this.get<WarmupProfileDto>(`/deliverability/warmup/${mailboxId}`);
  }

  public async startWarmup(mailboxId: string): Promise<WarmupProfileDto> {
    return this.post<WarmupProfileDto>(`/deliverability/warmup/${mailboxId}/start`, {});
  }

  public async pauseWarmup(mailboxId: string, reason?: string): Promise<WarmupProfileDto> {
    return this.post<WarmupProfileDto>(`/deliverability/warmup/${mailboxId}/pause`, { reason });
  }

  public async resumeWarmup(mailboxId: string): Promise<WarmupProfileDto> {
    return this.post<WarmupProfileDto>(`/deliverability/warmup/${mailboxId}/resume`, {});
  }

  public async updateWarmupConfig(mailboxId: string, dto: UpdateWarmupConfigRequest): Promise<WarmupProfileDto> {
    return this.patch<WarmupProfileDto>(`/deliverability/warmup/${mailboxId}/config`, dto);
  }

  public async getIpPools(): Promise<IpPoolDto[]> {
    return this.get<IpPoolDto[]>('/deliverability/ip-pools');
  }

  public async createIpPool(dto: CreateIpPoolRequest): Promise<IpPoolDto> {
    return this.post<IpPoolDto>('/deliverability/ip-pools', dto);
  }

  public async addIpToPool(poolId: string, dto: AddIpToPoolRequest): Promise<DedicatedIpDto> {
    return this.post<DedicatedIpDto>(`/deliverability/ip-pools/${poolId}/ips`, dto);
  }

  // ==========================================
  // SYSTEM HEALTH API
  // ==========================================

  public async getHealth(): Promise<HealthCheckResponse> {
    return this.get<HealthCheckResponse>('/health');
  }
}

export const apiClient = new ApiClient(env.apiBaseUrl);
